import {
    Align,
    Color,
    MaxWidth,
    TypographyAlign,
    TypographyColor,
    TypographyMaxWidth,
    alignOptions,
    colorOptions,
} from "../components/typography"
import { Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import ImageEditor, { AspectRatio as ImageAspectRatio } from "../Image"
import React, { useEffect, useRef } from "react"
import { Repeater, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../Typography"

import { ButtonBrickProps } from "../button/Button"
import { ButtonStyle } from "../components/button"
import { CloudflarePlayer } from "../components/video"
import { StreamPlayerApi } from "@cloudflare/stream-react"
import classNames from "classnames"
import { options } from "../../types"
import { twMerge } from "tailwind-merge"
import { useCheckForEmptyText } from "../../hooks/use-check-for-empty-text"
import { useIntersectionObserver } from "usehooks-ts"

export enum AspectRatio {
    "5/2" = "2.5",
    "16/9" = "1.77",
}

interface FullWidthVideoBreakProps {
    cloudflareVideoId: string
    cloudflareVideoIdMobile: string
    overlap: TypographyElement[]
    text: TypographyElement[]
    align: TypographyAlign
    color: { color: TypographyColor } | TypographyColor
    aspectRatio: AspectRatio
    textBelowVideo: boolean
    isDecorativeVideo?: boolean
    videoInBox?: boolean
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
}

interface PlayerProps {
    id: string
}

const Player = ({ id }: PlayerProps) => {
    const player = useRef<StreamPlayerApi>()

    const playerRef = useRef(null)

    const playerEntry = useIntersectionObserver(playerRef, {
        rootMargin: "1256px 256px 1256px 256px",
        freezeOnceVisible: true,
    })
    const playerIntersectionObserver = useIntersectionObserver(playerRef, { rootMargin: "256px 256px 256px 256px" })

    useEffect(() => {
        if (!playerIntersectionObserver || !player.current) return

        if (!playerIntersectionObserver.isIntersecting) {
            player.current.pause()
        } else {
            try {
                player.current.play()
            } catch (e) {
                player.current.muted = true
                player.current.play()
            }
        }
    }, [playerIntersectionObserver?.isIntersecting, player.current])

    if (!id) return null

    return (
        <div ref={playerRef} className="child:h-full">
            {playerEntry?.isIntersecting && <CloudflarePlayer id={id} streamRef={player} />}
        </div>
    )
}

const FullWidthVideoBreak: types.Brick<FullWidthVideoBreakProps> = ({
    cloudflareVideoId,
    cloudflareVideoIdMobile,
    isDecorativeVideo,
    align,
    color,
    aspectRatio,
    textBelowVideo,
    overlap,
    text,
    videoInBox,
    spacerTop,
    spacerBottom,
    variantTop = SpacerVariant.Small,
    variantBottom = SpacerVariant.Small,
}) => {
    const emptyOverlap = useCheckForEmptyText(overlap)
    const emptyText = useCheckForEmptyText(text)

    const txtColor = typeof color === "object" ? color.color : color

    return (
        <div>
            <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
            <div
                className={classNames("relative", {
                    "mx-auto w-full object-cover px-6.25 md:px-10 lg:max-w-[1560px] lg:px-15": videoInBox,
                })}
            >
                <div className="relative">
                    <div
                        className={classNames("w-full sm:hidden", {
                            "aspect-w-4 aspect-h-5": aspectRatio === AspectRatio["16/9"],
                            "aspect-w-5 aspect-h-4": aspectRatio === AspectRatio["5/2"],
                            "overflow-auto rounded-base": videoInBox,
                        })}
                        aria-hidden={isDecorativeVideo}
                    >
                        <ImageEditor
                            aspectRatio={
                                aspectRatio === AspectRatio["16/9"] ? ImageAspectRatio["4/5"] : ImageAspectRatio["5/4"]
                            }
                            maxWidth={768}
                            className={classNames("w-full", {
                                "z-2": !cloudflareVideoIdMobile,
                            })}
                            propName="image_mobile"
                        />
                        <Player id={cloudflareVideoIdMobile} />
                    </div>
                    <div
                        className={classNames("hidden sm:block", {
                            "aspect-w-16 aspect-h-9": aspectRatio === AspectRatio["16/9"],
                            "aspect-w-5 aspect-h-2": aspectRatio === AspectRatio["5/2"],
                            "overflow-hidden rounded-base": videoInBox,
                        })}
                        aria-hidden={isDecorativeVideo}
                    >
                        <ImageEditor
                            aspectRatio={
                                aspectRatio === AspectRatio["16/9"] ? ImageAspectRatio["16/9"] : ImageAspectRatio["5/2"]
                            }
                            maxWidth={1920}
                            className={classNames("w-full", {
                                "z-2": !cloudflareVideoId,
                            })}
                            propName="image"
                        />
                        <Player id={cloudflareVideoId} />
                    </div>
                </div>
                {(!emptyText || !emptyOverlap) && (
                    <div className="absolute top-1/2 z-2 w-full -translate-y-1/2 justify-center">
                        <Container>
                            <Align align={align}>
                                <MaxWidth
                                    smMaxWidth={TypographyMaxWidth.Full}
                                    mdMaxWidth={
                                        align === TypographyAlign.Center
                                            ? TypographyMaxWidth["70%"]
                                            : TypographyMaxWidth.Half
                                    }
                                >
                                    <Color color={txtColor}>
                                        {!emptyOverlap && (
                                            <Typography
                                                allowList={["h2", "h3", "sup", "link", "bold"]}
                                                propName="overlap"
                                            />
                                        )}
                                        {!emptyText && (
                                            <div
                                                className={classNames({
                                                    "mt-5 lg:mt-7": !emptyOverlap,
                                                    "hidden sm:block": !textBelowVideo,
                                                    "hidden md:block": textBelowVideo,
                                                })}
                                            >
                                                <Typography
                                                    allowList={[
                                                        "h2",
                                                        "h3",
                                                        "ul",
                                                        "small",
                                                        "sup",
                                                        "link",
                                                        "bold",
                                                        "checkList",
                                                    ]}
                                                />
                                            </div>
                                        )}
                                    </Color>
                                </MaxWidth>
                                <div
                                    className={twMerge(
                                        classNames({
                                            "hidden sm:block": !textBelowVideo,
                                            "hidden md:block": textBelowVideo,
                                            block: emptyText,
                                        })
                                    )}
                                >
                                    <Repeater
                                        propName="repeater"
                                        renderWrapper={children => <div className="mt-7 md:mt-10">{children}</div>}
                                    />
                                </div>
                            </Align>
                        </Container>
                    </div>
                )}
            </div>
            {!emptyText && (
                <div className={classNames({ "sm:hidden": !textBelowVideo, "md:hidden": textBelowVideo })}>
                    <Container>
                        <Spacer variant={SpacerVariant.Small} />
                        <Align align={TypographyAlign.Left}>
                            <Color>
                                <Typography
                                    allowList={["h2", "h3", "ul", "small", "sup", "link", "bold", "checkList"]}
                                />
                            </Color>
                        </Align>
                        <Repeater
                            propName="repeater"
                            renderWrapper={children => <div className="mt-7">{children}</div>}
                            itemProps={{ style: ButtonStyle.Primary } as ButtonBrickProps}
                        />
                        <Spacer variant={SpacerVariant.Small} />
                    </Container>
                </div>
            )}
            <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
        </div>
    )
}

FullWidthVideoBreak.schema = {
    name: "FullWidthVideoBreak",
    label: "Video Autoplay",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/FullWidthVideoBreak.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "Button",
                    label: "Button",
                    max: 1,
                },
                {
                    type: "TextLink",
                    label: "Text Link",
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "align",
            label: "Align",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: alignOptions,
                display: types.OptionsDisplay.Select,
            },
        },
        {
            name: "color",
            label: "Color",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: colorOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            name: "aspectRatio",
            label: "Aspect Ratio",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: options<typeof AspectRatio>(AspectRatio),
                display: types.OptionsDisplay.Select,
            },
        },
        {
            name: "cloudflareVideoId",
            label: "Cloudflare Video Id",
            type: types.SideEditPropType.Text,
        },
        {
            name: "cloudflareVideoIdMobile",
            label: "Cloudflare Video Id Mobile",
            type: types.SideEditPropType.Text,
        },
        {
            name: "isDecorativeVideo",
            label: "Is Decorative Video",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "textBelowVideo",
            label: "Text below Video on Tablet Portrait",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "videoInBox",
            label: "Video in Box",
            type: types.SideEditPropType.Boolean,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): FullWidthVideoBreakProps => ({
        overlap: [
            {
                type: "h2",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet",
                    },
                ],
            },
        ],
        text: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
                    },
                ],
            },
        ],
        cloudflareVideoId: "",
        cloudflareVideoIdMobile: "",
        isDecorativeVideo: false,
        align: TypographyAlign.Center,
        color: { color: TypographyColor.White },
        aspectRatio: AspectRatio["16/9"],
        textBelowVideo: false,
        videoInBox: false,
        spacerTop: false,
        spacerBottom: false,
        variantTop: SpacerVariant.Small,
        variantBottom: SpacerVariant.Small,
    }),
}

export default FullWidthVideoBreak
